import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Divider, IconButton, Paper, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import { capturePointer } from './pointerCapture';

export interface FloatingSideKickItem {
    title: string;
    icon: React.ReactNode;
    onClick: () => void;
    active?: boolean;
    disabled?: boolean;
}

export interface FloatingSideKickProps {
    /** Groups of tools, separated by dividers. */
    groups: FloatingSideKickItem[][];
    /** Bounds the menu can be dragged within (the stage). */
    boundsRef: React.RefObject<HTMLElement | null>;
    /** localStorage key remembering whether the menu shows labels. */
    storageKey: string;
    title?: string;
    expandedWidth?: number;
    ariaLabel?: string;
}

const EDGE = 12;

const readExpanded = (key: string): boolean => {
    try {
        return localStorage.getItem(key) === 'true';
    } catch {
        return false;
    }
};

/**
 * FloatingSideKick — floating, vertically stacked toolbar docked to the stage's top-left corner by
 * default. Icon-only by default; the chevron at the bottom expands it to show labels and the title.
 * Drag the grip to move it anywhere inside the stage.
 */
export const FloatingSideKick: React.FC<FloatingSideKickProps> = ({
    groups,
    boundsRef,
    storageKey,
    title = 'SIDEKICK',
    expandedWidth = 184,
    ariaLabel = 'SideKick menu',
}) => {
    const theme = useTheme();
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [pos, setPos] = useState<{ x: number; y: number }>({ x: EDGE, y: EDGE });
    const [expanded, setExpanded] = useState<boolean>(() => readExpanded(storageKey));
    const dragRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number } | null>(null);

    const clampToBounds = useCallback(
        (x: number, y: number) => {
            const bounds = boundsRef.current;
            const self = rootRef.current;
            if (!bounds || !self) return { x, y };
            const maxX = Math.max(EDGE, bounds.clientWidth - self.offsetWidth - EDGE);
            const maxY = Math.max(EDGE, bounds.clientHeight - self.offsetHeight - EDGE);
            return { x: Math.min(maxX, Math.max(EDGE, x)), y: Math.min(maxY, Math.max(EDGE, y)) };
        },
        [boundsRef],
    );

    // Keep the menu inside the stage when the stage (or the menu itself) resizes.
    useEffect(() => {
        const bounds = boundsRef.current;
        if (!bounds || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(() => setPos((p) => clampToBounds(p.x, p.y)));
        ro.observe(bounds);
        if (rootRef.current) ro.observe(rootRef.current);
        return () => ro.disconnect();
    }, [boundsRef, clampToBounds]);

    const toggleExpanded = () => {
        setExpanded((v) => {
            const next = !v;
            try {
                localStorage.setItem(storageKey, String(next));
            } catch {
                /* ignore */
            }
            return next;
        });
    };

    const onGripPointerDown = (e: React.PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: pos.x, baseY: pos.y };
        capturePointer(e);
    };
    const onGripPointerMove = (e: React.PointerEvent) => {
        const d = dragRef.current;
        if (!d) return;
        setPos(clampToBounds(d.baseX + (e.clientX - d.startX), d.baseY + (e.clientY - d.startY)));
    };
    const onGripPointerUp = (e: React.PointerEvent) => {
        dragRef.current = null;
        try {
            (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
        } catch {
            /* ignore */
        }
    };

    const tool = ({ title: label, icon, onClick, active = false, disabled = false }: FloatingSideKickItem) => {
        const colors = {
            color: active ? theme.palette.secondary.contrastText : theme.palette.text.primary,
            backgroundColor: active ? theme.palette.secondary.main : 'transparent',
            '&:hover': {
                backgroundColor: active ? theme.palette.secondary.dark : alpha(theme.palette.secondary.main, 0.16),
            },
        };
        if (expanded) {
            return (
                <Button
                    key={label}
                    size="small"
                    aria-label={label}
                    aria-pressed={active || undefined}
                    disabled={disabled}
                    onClick={onClick}
                    startIcon={icon}
                    fullWidth
                    sx={{ justifyContent: 'flex-start', textTransform: 'none', px: 1.25, whiteSpace: 'nowrap', ...colors }}
                >
                    {label}
                </Button>
            );
        }
        return (
            <Tooltip key={label} title={label} placement="right">
                <span>
                    <IconButton size="small" aria-label={label} aria-pressed={active || undefined} disabled={disabled} onClick={onClick} sx={colors}>
                        {icon}
                    </IconButton>
                </span>
            </Tooltip>
        );
    };

    return (
        <Paper
            ref={rootRef}
            elevation={6}
            role="toolbar"
            aria-label={ariaLabel}
            aria-orientation="vertical"
            data-expanded={expanded ? 'true' : 'false'}
            onPointerDown={(e) => e.stopPropagation()}
            sx={{
                position: 'absolute',
                left: pos.x,
                top: pos.y,
                zIndex: 30,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 0.25,
                p: 0.5,
                width: expanded ? expandedWidth : 'auto',
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.96),
                backdropFilter: 'blur(6px)',
                userSelect: 'none',
                transition: 'width 0.2s ease',
            }}
        >
            <Box
                aria-label={`Move ${ariaLabel}`}
                onPointerDown={onGripPointerDown}
                onPointerMove={onGripPointerMove}
                onPointerUp={onGripPointerUp}
                onPointerCancel={onGripPointerUp}
                sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: expanded ? 'space-between' : 'center',
                    cursor: 'grab',
                    color: 'text.secondary',
                    py: 0.25,
                    px: expanded ? 0.75 : 0,
                    touchAction: 'none',
                    '&:active': { cursor: 'grabbing' },
                }}
            >
                {expanded && (
                    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4 }}>
                        {title}
                    </Typography>
                )}
                <DragIndicatorIcon fontSize="small" sx={{ transform: expanded ? 'none' : 'rotate(90deg)' }} />
            </Box>
            {groups.map((group, i) => (
                <React.Fragment key={i}>
                    <Divider flexItem sx={{ my: 0.25 }} />
                    {group.map(tool)}
                </React.Fragment>
            ))}
            <Divider flexItem sx={{ my: 0.25 }} />
            <Tooltip title={expanded ? 'Collapse to icons' : 'Expand to show labels'} placement="right">
                <IconButton
                    size="small"
                    aria-label={expanded ? `Collapse ${ariaLabel}` : `Expand ${ariaLabel}`}
                    aria-expanded={expanded}
                    onClick={toggleExpanded}
                    sx={{ alignSelf: expanded ? 'flex-end' : 'center', color: 'text.secondary' }}
                >
                    {expanded ? <KeyboardDoubleArrowLeftIcon fontSize="small" /> : <KeyboardDoubleArrowRightIcon fontSize="small" />}
                </IconButton>
            </Tooltip>
        </Paper>
    );
};

export default FloatingSideKick;
